import React, {
    useState, useContext, useCallback, useMemo,
} from 'react';
import { useApolloClient } from '@apollo/client';
import PropTypes from 'prop-types';
import Raven from 'raven-js';

import GET_AB_TEST_VALUE from 'app/graphql/network/abTests';

import { LocaleContext } from 'app/containers/LocaleProvider';

const INITIAL_STATE = 'unfetched';
export const FETCHED_STATE = 'fetched';
export const ERROR_STATE = 'error';
const BY_HAND_STATE = 'byHand';

// BR-1147
export const NEW_TRIAL_UPPER_BLOCKS = 'new_trial_upper_blocks';
export const NEW_TRIAL_UPPDER_BLOCKS_TEST = 'test';
export const NEW_TRIAL_UPPDER_BLOCKS_CONTROL = 'control';

// BR-1195, Главная для auth, see: NearestDeliveryWidgetDA
export const MAIN_FOR_AUTH_USERS = 'main_for_auth_users';
export const MAIN_FOR_AUTH_USERS_TEST = 'new_main';
export const MAIN_FOR_AUTH_USERS_CONTROL = 'control';

// BR-1201
export const AFTER_FIVE_ORDERS = 'referral_after_five_orders';
export const AFTER_FIVE_ORDERS_TEST = 'referral_test_afo';
export const AFTER_FIVE_ORDERS_CONTROL = 'control';

export const AFTER_FOOD_SURVEY = 'referral_after_food_survey';
export const AFTER_FOOD_SURVEY_TEST = 'referral_test_afs';
export const AFTER_FOOD_SURVEY_CONTROL = 'control';

// BR-1199
export const COLORFUL_DESCRIPTION_IN_UPSELL = 'colorful_description_in_upsell';
export const COLORFUL_DESCRIPTION_IN_UPSELL_CONTROL = 'control';
export const COLORFUL_DESCRIPTION_IN_UPSELL_TEST = 'colorful';
// BR-1112
export const CASHBACK_NEW_USERS = 'cashback_new_users';
export const CASHBACK_NEW_USERS_CONTROL = 'control';
export const CASHBACK_NEW_USERS_TEST = 'with_cashback';

// BR-1192
export const CASHBACK_NEW_USERS_V2 = 'cashback_new_users_v2';
export const CASHBACK_NEW_USERS_V2_CONTROL = 'control';
export const CASHBACK_NEW_USERS_V2_TEST = 'with_cashback';

export const TRIAL_NO_TIME = 'trial_no_time';
export const TRIAL_NO_TIME_CONTROL = 'control';
export const TRIAL_NO_TIME_TEST = 'ai_trial_users';

// BR-1283
export const MENU_SORT_DATES = 'menu_sort_dates';
export const MENU_SORT_DATES_CONTROL = 'control';
export const MENU_SORT_DATES_TEST = 'test';

// BR-1258
export const NEW_MOBILE_MENU = 'new_mobile_menu';
export const NEW_MOBILE_MENU_CONTROL = 'control';

// BR-1264
export const UPSELL_AFTER_BASKET = 'upsell_after_basket';
export const UPSELL_AFTER_BASKET_CONTROL = 'control';
export const UPSELL_AFTER_BASKET_TEST = 'with_upsell';

const initialState = {
    /**
     * Перечислены все используемые ab-тесты и их дефолтные значения.
     * Очень желательно указывать дефолтное значение и все возможные группы здесь же.
     */
    [CASHBACK_NEW_USERS]: INITIAL_STATE,
    [CASHBACK_NEW_USERS_V2]: INITIAL_STATE,
    [NEW_TRIAL_UPPER_BLOCKS]: 'control',
    [AFTER_FIVE_ORDERS]: AFTER_FIVE_ORDERS_CONTROL,
    [AFTER_FOOD_SURVEY]: INITIAL_STATE,
    [MAIN_FOR_AUTH_USERS]: INITIAL_STATE,
    [COLORFUL_DESCRIPTION_IN_UPSELL]: INITIAL_STATE,
    [MAIN_FOR_AUTH_USERS]: MAIN_FOR_AUTH_USERS_CONTROL,

    [MENU_SORT_DATES]: INITIAL_STATE,
    [UPSELL_AFTER_BASKET]: INITIAL_STATE,
    // [UPSELL_AFTER_BASKET]: UPSELL_AFTER_BASKET_TEST,
    // SERVICE DATA
    /**
 * @description объект fetchingState хранит, каким образом было установлено состояние конкретного ab-теста.
 * Помогает в отладке и предотвращении лишних запросов по сети и изменений стейта.
 * Нет необходимости добавлять все ab-тесты в этот объект. При изменении значений теста флаг будет добавлен автоматически.
 * Возможные состояния: INITIAL_STATE, FETCHED_STATE, BY_HAND_STATE, ERROR_STATE.
 * INITIAL_STATE - исходное состояние. Важно определять его при добавлении нового ab-теста.
 * FETCHED_STATE - получено по сети.
 * ERROR_STATE - ошибка при получении по сети.
 * BY_HAND_STATE - значение установлено вручную.
*/
    fetchingState: {
        // [AB_BASKET_PREVIEW_IN_MENU]: INITIAL_STATE, // for example
        // [AB_DISH_SETTINGS_IN_BASKET]: INITIAL_STATE, // for example
    },
    /**
 * @description в error хранится объект ошибки при ошибке получении значения ab-теста по сети
 */
    error: null,
};

/**
 * объект, который позволяет мокать тесты
 * (запросы на такие тесты не будут отправляться в fetchABTestValue)
 */
const mockedTests = {};


export const abTestDataContext = React.createContext(initialState);

export const ABTestDataProvider = ({ children }) => {
    const [abState, setABState] = useState(initialState);
    const { locale } = useContext(LocaleContext);
    const client = useApolloClient();

    /**
     * @description Асинхронный метод для получения и установки аб-группы у аб-теста через запрос к бэку.
     * Выполняется только один раз для конкретного теста, после чего не меняет стейт и не выполняет лишних запросов.
     * Поведение:
     * - После получения значения сохраняет значение аб-группы в стейт и
     * устанавливает флаг FETCHED_STATE для этого аб-теста, чтобы предотвратить повторные запросы.
     * - Если произошла ошибка при получении данных, то устанавливается флаг ERROR_STATE для этого ab-теста и
     * возвращается дефолтное значение.
     * - Для английской версии ab-тесты не используются, возвращается исходное значение аб-группы.
     *
     * @returns {string} Возвращает значение ab-группы для использования сразу в месте вызова (только в async-функциях).
     *
     * @param {string} abTest - имя аб-теста, для которой нужно получить значение аб-группы.
    */
    const fetchABTestValue = useCallback(async (abTest) => {
        const { fetchingState } = abState;

        const isError = fetchingState[abTest] === ERROR_STATE;
        const isAlreadyFetched = fetchingState[abTest] === FETCHED_STATE;

        if (locale === 'en') return abState[abTest];
        if (isError) return abState[abTest];
        if (isAlreadyFetched) return abState[abTest];
        if (fetchingState[abTest] === 'fetching') return '';

        try {
            setABState((prevState) => ({
                ...prevState,
                fetchingState: { ...fetchingState, [abTest]: 'fetching' },
            }));
            let value;
            if (mockedTests[abTest] && window.location.host !== 'elementaree.ru') {
                value = mockedTests[abTest];
                console.warn(`abTest '${value}' is mocked`);
            } else {
                const { data } = await client.query({
                    query: GET_AB_TEST_VALUE,
                    variables: { abGroup: abTest },
                });

                value = data?.getABTestValue?.value;
            }
            sessionStorage.setItem(abTest, value);

            setABState((prevState) => ({
                ...prevState,
                [abTest]: value,
                fetchingState: { ...fetchingState, [abTest]: FETCHED_STATE },
            }));
            return value;
        } catch (error) {
            setABState((prevState) => ({
                ...prevState,
                fetchingState: { ...fetchingState, [abTest]: ERROR_STATE },
                error,
            }));

            Raven.captureException(error);

            return abState[abTest];
        }
    }, [client, locale, abState]);

    /**
     * @description метод для установки значения аб-теста вручную.
     * - Сохраняет значение в стейт и устанавливает флаг BY_HAND_STATE для этого ab-теста.
     * - При совпадении переданного значения с текущим не изменяет стейт.
     *
     * @returns {string} Возвращает значение ab-группы для использования сразу в месте вызова.
     *
     * @param {string} abTest - имя аб-теста, для которого устанавливается значение аб-группы.
     * @param {string} value - значение аб-группы.
    */
    const setABTestValue = useCallback((abTest, value) => {
        const { fetchingState, [abTest]: currentValue } = abState;

        if (value === currentValue) return currentValue;

        setABState((prevState) => ({
            ...prevState,
            [abTest]: value,
            fetchingState: { ...fetchingState, [abTest]: BY_HAND_STATE },
        }));

        return value;
    }, [abState]);

    const contextValue = useMemo(() => ({
        ...abState,
        fetchABTestValue,
        setABTestValue,
    }), [abState, fetchABTestValue, setABTestValue]);

    return (
        <abTestDataContext.Provider value={contextValue}>
            {children}
        </abTestDataContext.Provider>
    );
};

const withABTestData = (WrappedComponent) => (forwardedProps) => {
    const abTestData = useContext(abTestDataContext);

    return (
        <WrappedComponent
            abTestDataContext={abTestData}
            {...forwardedProps}
        />
    );
};

export default withABTestData;


ABTestDataProvider.propTypes = {
    children: PropTypes.element.isRequired,
};
